import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, FormEvent } from 'react';
import type { UserId } from '../types';
import { USER_LABELS } from '../types';
import { Spinner } from './Spinner';

interface LoginProps {
  configured: boolean;
  availableUsers: UserId[];
  onSubmit: (user: UserId, credential: string) => Promise<void>;
}

type Mode = 'password' | 'keyfile';

const LAST_USER_KEY = 'fox-image:last-user';

function loadLastUser(available: UserId[]): UserId {
  try {
    const stored = localStorage.getItem(LAST_USER_KEY);
    if (stored && available.includes(stored as UserId)) return stored as UserId;
  } catch {
    // ignore
  }
  return available[0] ?? 'kay';
}

export function Login({ configured, availableUsers, onSubmit }: LoginProps) {
  const users = availableUsers.length > 0 ? availableUsers : (['kay', 'lia'] as UserId[]);
  const [user, setUser] = useState<UserId>(() => loadLastUser(users));
  const [mode, setMode] = useState<Mode>('password');
  const [password, setPassword] = useState('');
  const [keyText, setKeyText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    try {
      localStorage.setItem(LAST_USER_KEY, user);
    } catch {
      // ignore
    }
  }, [user]);

  const handleKeyFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024) {
      setError('Key file is unexpectedly large. Choose a small .txt file.');
      return;
    }
    try {
      const text = await file.text();
      setKeyText(text.trim());
    } catch {
      setError('Could not read key file.');
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const credential = mode === 'password' ? password : keyText;
    if (!credential.trim()) {
      setError(mode === 'password' ? 'Enter your password.' : 'Provide your key.');
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(user, credential.trim());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-[100dvh] max-w-md flex-col justify-center px-5 py-12">
      <div className="card">
        <header className="mb-6 flex items-center gap-3">
          <div
            aria-hidden="true"
            className="grid h-10 w-10 place-items-center rounded-xl"
            style={{
              background: 'linear-gradient(135deg, var(--color-app-accent), var(--color-app-accent-2))',
            }}
          >
            <svg viewBox="0 0 24 24" width="22" height="22" fill="#0b0b0f">
              <path d="M5 17 9 9l4 5 3-3 4 6Z" />
              <circle cx="16" cy="6" r="2" />
            </svg>
          </div>
          <div>
            <h1 className="text-lg font-semibold leading-none">fox-image</h1>
            <p className="text-xs text-muted">Private uploader · Cloudflare R2</p>
          </div>
        </header>

        {!configured && (
          <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
            Server is not fully configured. Set <code className="font-mono">SESSION_SECRET</code> and at least one user
            password (<code className="font-mono">KAY_PASSWORD</code> /{' '}
            <code className="font-mono">LIA_PASSWORD</code>) before logging in.
          </div>
        )}

        <div className="mb-4">
          <p className="label mb-2">Who's signing in?</p>
          <div className="grid grid-cols-2 gap-2">
            {users.map((u) => (
              <button
                key={u}
                type="button"
                onClick={() => setUser(u)}
                aria-pressed={user === u}
                className={`rounded-md border px-3 py-2 text-sm font-medium transition ${
                  user === u
                    ? 'border-amber-400 bg-amber-500/10 text-app'
                    : 'border-app-border text-muted hover:text-app hover:border-app-muted'
                }`}
              >
                {USER_LABELS[u]}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-4 inline-flex rounded-md border border-app-border p-1 text-xs" role="tablist">
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'password'}
            onClick={() => setMode('password')}
            className={`rounded px-3 py-1 transition ${
              mode === 'password' ? 'bg-app-surface-2 text-app' : 'text-muted hover:text-app'
            }`}
          >
            Password
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === 'keyfile'}
            onClick={() => setMode('keyfile')}
            className={`rounded px-3 py-1 transition ${
              mode === 'keyfile' ? 'bg-app-surface-2 text-app' : 'text-muted hover:text-app'
            }`}
          >
            Key file
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'password' ? (
            <div>
              <label className="label mb-2" htmlFor="password">
                {USER_LABELS[user]}'s password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                className="field focus-ring"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={submitting}
                autoFocus
              />
            </div>
          ) : (
            <div className="space-y-2">
              <label className="label" htmlFor="keyfile">
                {USER_LABELS[user]}'s key file (.txt)
              </label>
              <div className="flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  id="keyfile"
                  type="file"
                  accept=".txt,text/plain"
                  className="hidden"
                  onChange={handleKeyFile}
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={submitting}
                >
                  Choose file
                </button>
                {keyText && (
                  <span className="pill" title={`${keyText.length} chars`}>
                    Loaded · {keyText.length} chars
                  </span>
                )}
              </div>
              <textarea
                aria-label="Or paste key contents"
                placeholder="…or paste key contents"
                className="field focus-ring h-20 font-mono text-xs"
                value={keyText}
                onChange={(e) => setKeyText(e.target.value)}
                disabled={submitting}
              />
            </div>
          )}

          {error && (
            <div role="alert" className="rounded-md border border-rose-500/40 bg-rose-500/10 px-3 py-2 text-sm text-rose-200">
              {error}
            </div>
          )}

          <button type="submit" className="btn btn-primary w-full" disabled={submitting}>
            {submitting ? <Spinner size={14} /> : null}
            <span>{submitting ? 'Signing in…' : `Sign in as ${USER_LABELS[user]}`}</span>
          </button>

          <p className="text-center text-xs text-muted">
            Sessions last 30 days. Cookies are HttpOnly + Secure.
          </p>
        </form>
      </div>
    </div>
  );
}
